import { trackingFeatureDisplayEvent } from '@/tracking/tracking';
import { onBeforeUnmount, onMounted, Ref } from 'vue';

/**
 * Tracks when an element becomes visible in the viewport by firing a `feature_displayed` GTM event.
 *
 * Uses `IntersectionObserver` under the hood. The event fires **once** the element reaches
 * the configured visibility threshold, and the observer is disconnected immediately after to avoid
 * duplicate events. If the element is unmounted before becoming visible, the observer is cleaned up automatically.
 *
 * @param targetRef  - A template ref pointing to the element to observe.
 * @param action     - The tracking action string (maps to `h_event_action_007`). Use a value from `TRACKING_ACTION`.
 * @param label      - The tracking label string (maps to `h_event_label_008`). Use a value from `TRACKING_LABEL`.
 * @param threshold  - How much of the element must be visible (0–1) before the event fires. Defaults to `0.7` (70%).
 * @param params     - Optional extra GTM params merged into the feature display event.
 *                     Pass a getter to resolve params when the element becomes visible.
 */
export function useTrackOnVisible(
  targetRef: Ref<HTMLElement | null>,
  action: string,
  label: string,
  threshold = 0.7,
  params?: Record<string, string> | (() => Record<string, string>),
) {
  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    if (!(targetRef.value instanceof HTMLElement)) return;

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= threshold && observer) {
          const resolvedParams = typeof params === 'function' ? params() : params;
          trackingFeatureDisplayEvent(action, label, resolvedParams);
          observer.disconnect();
          observer = null;
        }
      },
      { threshold },
    );

    observer.observe(targetRef.value);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
  });
}
